import { useRef, useState, type JSX } from 'react'
import { useAgents } from '../../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import DurationInput from '../DurationInput.js'
import Select from '../../ui/Select.js'

export interface Level {
  level: number
  approverAgentId?: string
  approverQueueId?: string
  timeoutMinutes: number
}

export interface ApprovalLevelsEditorProps {
  value: Level[]
  onChange: (next: Level[]) => void
  /** Maximum number of levels allowed. Default 10. */
  maxLevels?: number
}

const DEFAULT_TIMEOUT_MINUTES = 60

function blankLevel(position: number): Level {
  return {
    level: position,
    timeoutMinutes: DEFAULT_TIMEOUT_MINUTES,
  }
}

/** Renumber every level so `level` equals 1-based position in the array. */
function renumber(levels: Level[]): Level[] {
  return levels.map((lv, idx) => ({ ...lv, level: idx + 1 }))
}

/**
 * Controlled approval-levels editor.
 *
 * Each level has:
 *   - A level-number badge (auto-assigned from row order).
 *   - An Agent / Queue radio that toggles which approver type is set.
 *   - An agent or queue <select> dropdown.
 *   - A DurationInput for the timeout.
 *   - A remove button.
 *
 * A drag handle (HTML5 draggable) allows row reordering.
 * At most `maxLevels` (default 10) levels are allowed — the add button is
 * disabled when the limit is reached.
 *
 * Inline validation shows an error for any level that has neither
 * approverAgentId nor approverQueueId.
 *
 * ### External `value` replacement contract
 * `rowTypes` is initialised once at mount from `value` and is thereafter
 * maintained purely via index-mirror operations (append / filter / splice)
 * that match the structural mutations of the levels array.  It is NEVER
 * re-derived from the incoming `value` prop post-mount.
 *
 * If the parent needs to swap `value` wholesale (e.g. loading a different
 * workflow or resetting the form), it MUST remount this component by
 * changing its React `key` prop.  This avoids the impossible problem of
 * distinguishing an external replacement from an `onChange` round-trip
 * (both arrive as new `value` reference identity).
 */
export default function ApprovalLevelsEditor({
  value,
  onChange,
  maxLevels = 10,
}: ApprovalLevelsEditorProps): JSX.Element {
  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()

  // Track which index is being dragged (null = not dragging).
  const dragIndexRef = useRef<number | null>(null)

  // -------------------------------------------------------------------------
  // UI state: which radio (agent|queue) is selected per row.
  // Decoupled from emitted data so toggling doesn't put '' into the payload.
  // Initialized from value: rows with an existing approverQueueId start as 'queue'.
  // -------------------------------------------------------------------------
  const [rowTypes, setRowTypes] = useState<('agent' | 'queue')[]>(() =>
    value.map((lv) =>
      'approverQueueId' in lv && lv.approverQueueId !== undefined ? 'queue' : 'agent',
    ),
  )

  // -------------------------------------------------------------------------
  // Mutations — all produce a renumbered next array.
  // rowTypes is kept as a true index-mirror of the levels array via the same
  // structural operation (append / filter / splice-reorder) — never re-derived
  // from data after the initial mount.
  // -------------------------------------------------------------------------

  function addLevel(): void {
    if (value.length >= maxLevels) return
    const next = renumber([...value, blankLevel(value.length + 1)])
    onChange(next)
    setRowTypes((prev) => [...prev, 'agent'])
  }

  function removeAt(i: number): void {
    const next = renumber(value.filter((_, idx) => idx !== i))
    onChange(next)
    setRowTypes((prev) => prev.filter((_, idx) => idx !== i))
  }

  function setApproverType(i: number, type: 'agent' | 'queue'): void {
    // Update UI radio state only — do NOT put '' into the data payload.
    setRowTypes((prev) => prev.map((t, idx) => (idx === i ? type : t)))
    // Emit a level with NEITHER approver key (both omitted).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring to omit both approver keys from the spread; _a/_q are intentionally discarded
    const { approverAgentId: _a, approverQueueId: _q, ...base } = value[i]!
    const next: Level = { ...base }
    onChange(renumber(value.map((lv, idx) => (idx === i ? next : lv))))
  }

  // -------------------------------------------------------------------------
  // HTML5 drag-reorder (same pattern as RoutingRulesPage)
  // -------------------------------------------------------------------------

  function onDragStart(index: number): void {
    dragIndexRef.current = index
  }

  function onDragOver(e: React.DragEvent): void {
    e.preventDefault()
  }

  function onDrop(targetIndex: number): void {
    const dragIndex = dragIndexRef.current
    dragIndexRef.current = null
    if (dragIndex === null || dragIndex === targetIndex) return
    const next = [...value]
    const [moved] = next.splice(dragIndex, 1)
    if (moved !== undefined) next.splice(targetIndex, 0, moved)
    const renumbered = renumber(next)
    onChange(renumbered)
    // Mirror the same reorder in rowTypes.
    const nextTypes = [...rowTypes]
    const [movedType] = nextTypes.splice(dragIndex, 1)
    if (movedType !== undefined) nextTypes.splice(targetIndex, 0, movedType)
    setRowTypes(nextTypes)
  }

  const atMax = value.length >= maxLevels

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="ale flex flex-col gap-2">
      {value.length === 0 && (
        <p className="text-sm text-gray-500">No approval levels configured.</p>
      )}
      <ul role="list" className="flex flex-col gap-2">
        {value.map((lv, i) => {
          const approverType: 'agent' | 'queue' = rowTypes[i] ?? 'agent'

          const missingApprover =
            (lv.approverAgentId === undefined || lv.approverAgentId === '') &&
            (lv.approverQueueId === undefined || lv.approverQueueId === '')

          return (
            <li
              key={i}
              role="listitem"
              className="ale-row flex flex-wrap items-center gap-2 rounded border border-gray-300 p-2"
            >
              {/* Drag handle */}
              <button
                type="button"
                draggable
                aria-label={`Drag handle level ${lv.level}`}
                className="cursor-move select-none rounded px-1 text-gray-400 hover:text-gray-600"
                onDragStart={() => onDragStart(i)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(i)}
              >
                ☰
              </button>

              {/* Level number badge */}
              <span
                aria-label={`Level number ${lv.level}`}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700"
              >
                {lv.level}
              </span>

              {/* Agent / Queue radio */}
              <fieldset className="flex items-center gap-1 border-0 p-0">
                <legend className="sr-only">Approver type for level {lv.level}</legend>
                <label className="flex items-center gap-0.5 text-xs">
                  <input
                    type="radio"
                    name={`approver-type-${i}`}
                    value="agent"
                    checked={approverType === 'agent'}
                    aria-label={`Agent ${lv.level}`}
                    onChange={() => setApproverType(i, 'agent')}
                    className="mr-0.5"
                  />
                  Agent
                </label>
                <label className="flex items-center gap-0.5 text-xs">
                  <input
                    type="radio"
                    name={`approver-type-${i}`}
                    value="queue"
                    checked={approverType === 'queue'}
                    aria-label={`Queue ${lv.level}`}
                    onChange={() => setApproverType(i, 'queue')}
                    className="mr-0.5"
                  />
                  Queue
                </label>
              </fieldset>

              {/* Agent dropdown */}
              {approverType === 'agent' && (
                                <Select
                  ariaLabel={`Approver agent ${lv.level}`}
                  size="sm"
                  value={lv.approverAgentId ?? ''}
                  onChange={(v) => {
                    const agentId = v
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring to strip both approver keys before setting only the newly selected agent; _a/_q intentionally discarded
                    const { approverAgentId: _a, approverQueueId: _q, ...base } = lv
                    const next: Level = agentId ? { ...base, approverAgentId: agentId } : { ...base }
                    onChange(renumber(value.map((row, idx) => (idx === i ? next : row))))
                  }}
                >
                  <option value="">— select agent —</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.extension}
                    </option>
                  ))}
                                </Select>
              )}

              {/* Queue dropdown */}
              {approverType === 'queue' && (
                                <Select
                  ariaLabel={`Approver queue ${lv.level}`}
                  size="sm"
                  value={lv.approverQueueId ?? ''}
                  onChange={(v) => {
                    const queueId = v
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring to strip both approver keys before setting only the newly selected queue; _a/_q intentionally discarded
                    const { approverAgentId: _a, approverQueueId: _q, ...base } = lv
                    const next: Level = queueId ? { ...base, approverQueueId: queueId } : { ...base }
                    onChange(renumber(value.map((row, idx) => (idx === i ? next : row))))
                  }}
                >
                  <option value="">— select queue —</option>
                  {queues.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name}
                    </option>
                  ))}
                                </Select>
              )}

              {/* Timeout duration */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-600">Timeout:</span>
                <DurationInput
                  value={lv.timeoutMinutes}
                  onChange={(totalMinutes) => {
                    onChange(renumber(value.map((row, idx) =>
                      idx === i ? { ...row, timeoutMinutes: totalMinutes } : row,
                    )))
                  }}
                  min={1}
                />
              </div>

              {/* Inline validation error */}
              {missingApprover && (
                <p role="alert" className="w-full text-xs text-red-600">
                  An approver (agent or queue) is required for this level.
                </p>
              )}

              {/* Remove button */}
              <button
                type="button"
                aria-label={`Remove level ${lv.level}`}
                className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
                onClick={() => removeAt(i)}
              >
                ×
              </button>
            </li>
          )
        })}
      </ul>

      <div>
        <button
          type="button"
          disabled={atMax}
          aria-label="Add level"
          className="rounded border border-gray-300 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40"
          onClick={addLevel}
        >
          + Add level
        </button>
        {atMax && (
          <p className="mt-1 text-xs text-gray-500">
            Maximum of {maxLevels} levels reached.
          </p>
        )}
      </div>
    </div>
  )
}
